import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { toggleEditMode } from '../../../shared/utils';
import { ILdstellenResult, ILookupValue } from '../../models/lookupvalues';
import { IVersicherungsbestaetigung } from '../../models/versicherungsbestaetigung';
import { KeyValueService } from '../../service/keyvalue.service';
import { LookupService } from '../../service/lookup.service';
import { PopupService } from '../../service/popup.service';

@Component({
  selector: 'app-versicherungsbestaetigung',
  templateUrl: './versicherungsbestaetigung.component.html',
  styleUrls: ['./versicherungsbestaetigung.component.scss']
})
export class VersicherungsbestaetigungComponent implements OnInit, OnChanges {
  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  beginndatum: Date;

  @Input()
  region: ILookupValue;

  @Input()
  antrag: boolean;

  @Input()
  motorrad: boolean;

  @Input()
  expertMode: boolean;

  @Input()
  ldstellen: ReplaySubject<ILdstellenResult>;

  vbvergabearten: Map<number, string>;

  constructor(public translateService: TranslateService, public popupService: PopupService, public lookupService: LookupService, public keyValueService: KeyValueService) { }

  static createFormGroup(fb: UntypedFormBuilder) {
    return fb.group({
      vbvergabeart: new UntypedFormControl('', Validators.required),
      vbnummer: new UntypedFormControl(''),
      ldstelle: new UntypedFormControl('')
    });
  }

  ngOnInit(): void {
    this.vbvergabearten = this.expertMode ?
      new Map([
        [1, 'Vermittler (VB wird per Mail verschickt)'],
        [3, 'Vermittler (hinterlegt vorhandene VB selbst)'],
        [2, 'HDI (VB wird direkt durch HDI im System eingegeben)']
      ]) : new Map([
        [1, 'Versand per E-Mail an die oben eingegebene Adresse'],
        [3, 'Versicherungsbestätigung soll direkt hinterlegt werden (keine Abholung erforderlich)'],
        [2, 'Versicherungsbestätigung soll zur Abholung bereitgelegt werden']
      ]);

    this.onValueChanged();
    this.toggleEditMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.region && changes.region.currentValue !== changes.region.previousValue) {
      this.ldstellen.pipe(first()).toPromise().then(res => {
        this.ldstelle.setValue(res.Ldstellen.find(x => x.Id === res.BezirkLdstelle
          .find(x => x.BezirkCode === changes.region.currentValue?.Id)?.StandortId?.toString()));
      });
    }
  }

  get vbvergabeart() { return this.formGroup.get('vbvergabeart'); }
  get vbnummer() { return this.formGroup.get('vbnummer'); }
  get ldstelle() { return this.formGroup.get('ldstelle'); }

  get showVersicherungsbestaetigung() {
    return this.expertMode || !this.expertMode && this.antrag;
  }
  get showVbnummer() {
    return this.expertMode && this.vbvergabeart.value === 3;
  }
  get showLdstelle() {
    return !this.expertMode && this.vbvergabeart.value === 2;
  }

  toggleEditMode(): void {
    toggleEditMode(this.vbvergabeart, this.showVersicherungsbestaetigung);
    toggleEditMode(this.vbnummer, this.showVbnummer);
    toggleEditMode(this.ldstelle, this.showLdstelle);
  }

  onValueChanged(): void {
    this.vbvergabeart.valueChanges.subscribe(() => {
      this.toggleEditMode();
    });
  }

  setVersicherungsbestaetigung(versicherungsbestaetigung: IVersicherungsbestaetigung) {
    this.vbvergabeart.setValue(versicherungsbestaetigung.Vbvergabeart);
    this.vbnummer.setValue(versicherungsbestaetigung.Vbnummer);
    if (versicherungsbestaetigung.LdstelleId) {
      this.ldstellen.pipe(first()).toPromise().then((ldstellenResult) => {
        this.ldstelle.setValue(ldstellenResult.Ldstellen.find(x => x.Id === versicherungsbestaetigung.LdstelleId.toString()));
      });
    }
  }

  getVersicherungsbestaetigung(): IVersicherungsbestaetigung {
    return {
      Vbvergabeart: this.vbvergabeart.value ? this.vbvergabeart.value : null,
      Vbnummer: this.vbnummer.value,
      LdstelleId: this.ldstelle.value ? this.ldstelle.value.Id : null
    };
  }
}
